/**
 * Arthaus Product Detail (PDP) module
 * Phase: 4 (refactor)
 *
 * Exposes window.ArthausPDP with a re-callable init() so the frame-switcher
 * can re-bind the panel + gallery after an in-place product swap
 * (see assets/frame-switcher.js).
 *
 * Responsibilities:
 *   - Variant option selectors (Size / Frame Colour / Borders / Paper Type …)
 *     → resolve to a variant, update price, availability, hidden input, URL
 *   - Image gallery: thumbnail swap + lightbox
 *   - Ajax add-to-cart → dispatches `cart:updated`
 *
 * Document/window-level listeners (lightbox keyboard nav) are bound ONCE at
 * load; element-level listeners are (re)bound by init() and die with the DOM
 * they were attached to, so swapping is leak-free.
 */
window.ArthausPDP = (function () {
  'use strict';

  // The symbol comes from the server-rendered price (the shop's own money format), so a
  // client-side price update keeps the currency the page loaded with.
  var moneySymbol = '$';
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return moneySymbol + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  }
  // A v2 variant carries its presentment price pre-formatted by the server
  // (snippets/product-config); `price: null` means no presentment price is known, and then
  // NO price is shown — never the base-currency number with this page's symbol.
  function variantPrice(v) {
    if (v.price_formatted !== undefined) return v.price_formatted || '';
    if (v.price == null) return '';
    return formatMoney(v.price);
  }
  function variantCompare(v) {
    if (v.price == null || !v.compare_at_price || v.compare_at_price <= v.price) return '';
    if (v.compare_at_formatted !== undefined) return v.compare_at_formatted || '';
    return formatMoney(v.compare_at_price);
  }

  // ── shared lightbox state (the lightbox element persists across swaps) ──
  var lightbox = null;
  var lightboxImg = null;
  var lb = { images: [], index: 0 };
  var lbReturnFocus = null;   // element to refocus when the viewer closes

  // Gallery API for the current DOM, set by initGallery() and consumed by
  // initConfig() to push the selected variant's image into slide 0.
  var galleryApi = null;

  function showLightboxImage() {
    var im = lb.images[lb.index];
    if (!im || !lightboxImg) return;
    lightboxImg.src = im.full || im.src;
    lightboxImg.alt = im.alt || '';
    if (im.width) lightboxImg.width = im.width;
    if (im.height) lightboxImg.height = im.height;
  }
  // Modal semantics (HD-P0-06): focus moves into the viewer, Tab is trapped
  // inside it, Escape closes, and focus returns to whatever opened it.
  function lightboxFocusables() {
    return Array.prototype.slice.call(lightbox.querySelectorAll('button:not([disabled])'));
  }
  function openLightbox(invoker) {
    if (!lightbox) return;
    lbReturnFocus = invoker || document.activeElement;
    showLightboxImage();
    lightbox.hidden = false;
    // Apply flex via inline style (not a static class) so the `hidden`
    // attribute reliably hides it — Tailwind's `[hidden]:where(){display:none}`
    // reset has zero specificity and would otherwise lose to a `.flex` class.
    lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    (closeBtn || lightbox).focus();
  }
  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    lightbox.style.display = '';
    lightbox.hidden = true;
    document.body.style.overflow = '';
    if (lbReturnFocus && document.contains(lbReturnFocus) && lbReturnFocus.focus) lbReturnFocus.focus();
    lbReturnFocus = null;
  }
  function navLightbox(dir) {
    if (!lb.images.length) return;
    lb.index = (lb.index + dir + lb.images.length) % lb.images.length;
    showLightboxImage();
  }
  function bindLightboxOnce() {
    lightbox = document.getElementById('product-lightbox');
    lightboxImg = document.getElementById('lightbox-image');
    if (!lightbox) return;
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    var prev = lightbox.querySelector('[data-lightbox-prev]');
    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); navLightbox(-1); });
    var next = lightbox.querySelector('[data-lightbox-next]');
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); navLightbox(1); });
    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navLightbox(-1);
      if (e.key === 'ArrowRight') navLightbox(1);
      if (e.key === 'Tab') {
        var f = lightboxFocusables();
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || !lightbox.contains(document.activeElement))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !lightbox.contains(document.activeElement))) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  // ── Gallery (thumbnail swap + lightbox trigger) ────────────────────────
  function initGallery(scope) {
    var hero = scope && scope.querySelector ? scope.querySelector('[data-product-hero]') : null;
    if (!hero && scope && scope.matches && scope.matches('[data-product-hero]')) hero = scope;
    if (!hero) hero = document.querySelector('[data-product-hero]');
    if (!hero) return;

    var primaryImg = hero.querySelector('#product-primary-image');
    var thumbs = Array.prototype.slice.call(hero.querySelectorAll('[data-thumb-index]'));
    var images = [];

    if (thumbs.length) {
      thumbs.forEach(function (t) {
        images.push({
          src: t.getAttribute('data-thumb-src'),
          full: t.getAttribute('data-thumb-full'),
          srcset: t.getAttribute('data-thumb-srcset'),
          alt: t.getAttribute('data-thumb-alt'),
          width: t.getAttribute('data-thumb-width'),
          height: t.getAttribute('data-thumb-height'),
          aspect: t.getAttribute('data-thumb-aspect')
        });
      });
    } else if (primaryImg) {
      images.push({
        src: primaryImg.getAttribute('src'),
        srcset: primaryImg.getAttribute('srcset'),
        alt: primaryImg.getAttribute('alt'),
        width: primaryImg.getAttribute('width'),
        height: primaryImg.getAttribute('height'),
        aspect: primaryImg.style.aspectRatio
      });
    }

    // Slide 0 is "variant-driven": when a variant with its own image is selected,
    // it overrides the default first slide (the template-composite); deselecting
    // (a variant without an image) reverts to images[0].
    var variantOverride = null;
    function slideAt(i) { return (i === 0 && variantOverride) ? variantOverride : images[i]; }

    var current = 0;
    function setImage(i) {
      var im = slideAt(i);
      if (!primaryImg || !im) return;
      current = i;
      primaryImg.src = im.src;
      primaryImg.srcset = im.srcset || '';
      primaryImg.alt = im.alt || '';
      if (im.width) primaryImg.width = im.width;
      if (im.height) primaryImg.height = im.height;
      if (im.aspect) primaryImg.style.aspectRatio = im.aspect;
      thumbs.forEach(function (t, j) {
        t.classList.toggle('border-charcoal', j === i);
        t.classList.toggle('border-transparent', j !== i);
        t.setAttribute('aria-pressed', String(j === i));
      });
    }
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { setImage(i); });
    });

    var mainArea = hero.querySelector('[data-gallery-main]');
    if (mainArea) {
      var openViewer = function () {
        lb.images = images.map(function (im, idx) { return (idx === 0 && variantOverride) ? variantOverride : im; });
        lb.index = current;
        openLightbox(mainArea);
      };
      mainArea.addEventListener('click', openViewer);
      // It is a role="button" <div>: Enter/Space must activate it like a button.
      mainArea.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openViewer(); }
      });
    }

    // Exposed to initConfig: set/clear the variant image shown in slide 0.
    galleryApi = {
      setVariantImage: function (im) {
        variantOverride = im || null;
        setImage(0); // jump to + render the (now variant-specific) first slide
      }
    };
  }

  // ── Config panel (options → variant, price, add-to-cart) ───────────────
  function initConfig(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var configEl = root.querySelector('[data-product-config]');
    if (!configEl) return;

    var variants = [];
    var json = configEl.querySelector('[data-variants-json]');
    if (json) {
      try { variants = JSON.parse(json.textContent); } catch (e) { variants = []; }
    }

    // Optional variant → image map (from the gallery), keyed by variant id, used to
    // drive slide 0 when the shopper changes options. Absent → swap is a no-op.
    var variantImages = {};
    var vimgEl = (scope && scope.querySelector && scope.querySelector('[data-variant-images]')) ||
                 document.querySelector('[data-variant-images]');
    if (vimgEl) {
      try {
        (JSON.parse(vimgEl.textContent) || []).forEach(function (im) { if (im && im.id != null) variantImages[im.id] = im; });
      } catch (e) { variantImages = {}; }
    }

    var priceEl      = configEl.querySelector('[data-current-price]');
    var compareEl    = configEl.querySelector('[data-compare-price]');
    var variantInput = configEl.querySelector('[data-variant-input]');
    var addBtn       = configEl.querySelector('[data-add-to-cart-btn]');
    var addBtnText   = configEl.querySelector('[data-add-to-cart-text]');
    var form         = configEl.querySelector('[data-add-to-cart-form]');
    var groups       = Array.prototype.slice.call(configEl.querySelectorAll('[data-option-group]'));

    if (priceEl) {
      var sym = (priceEl.textContent || '').trim().match(/^[^\d\s]+/);
      if (sym) moneySymbol = sym[0];
    }

    // Product System v2: the variants are an artwork's offers, each naming the
    // artist-product variant to add (`id`) plus its canonical SKU and signed quote. The
    // line properties shared by every variant are rendered by snippets/product-config.
    var v2Line = null;
    var v2LineEl = configEl.querySelector('[data-v2-line]');
    if (v2LineEl) {
      try { v2Line = JSON.parse(v2LineEl.textContent); } catch (e) { v2Line = null; }
    }
    var currentVariant = null;

    // Sticky add-to-cart bar (snippets/sticky-cta-bar.liquid, rendered in the hero).
    // It mirrors this panel's price + button state and forwards its click to the
    // real form, so there is one add-to-cart path. Visibility: sticky-cta-bar.js.
    var stickyBar  = document.querySelector('[data-product-hero] [data-sticky-cta-bar]');
    var stickyBtn  = stickyBar && stickyBar.querySelector('[data-sticky-cta-btn]');
    var stickyText = stickyBar && stickyBar.querySelector('[data-sticky-cta-text]');
    var stickyPrice = stickyBar && stickyBar.querySelector('[data-sticky-price]');

    function setCtaText(text) {
      if (addBtnText) addBtnText.textContent = text;
      if (stickyText) stickyText.textContent = text;
    }
    function setCtaDisabled(disabled) {
      if (addBtn) addBtn.disabled = disabled;
      if (stickyBtn) stickyBtn.disabled = disabled;
    }
    if (stickyBtn && form) {
      stickyBtn.addEventListener('click', function () {
        if (form.requestSubmit) form.requestSubmit(addBtn || undefined);
        else if (addBtn) addBtn.click();
      });
    }
    if (stickyBar && window.ArthausStickyBar) window.ArthausStickyBar.init(stickyBar.parentNode);

    // selected[position] = value
    var selected = [];
    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      var pressed = group.querySelector('[data-option-value][aria-pressed="true"]') ||
                    group.querySelector('[data-option-value]');
      if (pressed) selected[pos] = pressed.getAttribute('data-value');
    });

    function findVariant() {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var opts = v.options || [];
        var match = true;
        for (var j = 0; j < opts.length; j++) {
          if (selected[j + 1] !== opts[j]) { match = false; break; }
        }
        if (match && opts.length) return v;
      }
      // single-option / default-variant products
      return variants.length === 1 ? variants[0] : null;
    }

    // Selected appearance is CSS-driven via `aria-pressed:` Tailwind variants,
    // so toggling the attribute is enough — works for pills and colour swatches alike.
    function setPressed(btn, on) {
      btn.setAttribute('aria-pressed', String(on));
    }

    // Mirror the chosen value into the group's "selected value" label (colour swatches).
    function updateSelectedLabel(pos, value) {
      var label = configEl.querySelector('[data-selected-label][data-for-position="' + pos + '"]');
      if (label) label.textContent = value;
    }

    function showPrice(v) {
      if (priceEl) priceEl.textContent = variantPrice(v);
      if (stickyPrice) stickyPrice.textContent = variantPrice(v);
      if (compareEl) {
        var cmp = variantCompare(v);
        if (cmp) {
          compareEl.textContent = cmp;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    // v2 offers Liquid could not price server-side (an artist product past Liquid's 250
    // variants — snippets/v2-price-map) arrive with `price: null`. Fetch each one's
    // presentment price from the artist product (templates/product.v2-price.liquid, which
    // selects any variant via ?variant=), then fill in the main / sticky price, the size
    // chips and this format's tab. Until then — or if a fetch fails — that price stays hidden.
    function fillMissingPrices() {
      var priceUrl = configEl.getAttribute('data-v2-price-url');
      if (!v2Line || !priceUrl || !window.fetch) return;
      var missing = variants.filter(function (v) { return v && v.id && v.price == null; });
      if (!missing.length) return;
      var sep = priceUrl.indexOf('?') > -1 ? '&' : '?';
      Promise.all(missing.map(function (v) {
        return fetch(priceUrl + sep + 'variant=' + encodeURIComponent(v.id) + '&view=v2-price', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (!p || String(p.id) !== String(v.id) || p.price == null) return;
            v.price = p.price;
            v.compare_at_price = p.compare_at_price;
            v.price_formatted = p.price_formatted;
            v.compare_at_formatted = p.compare_at_formatted;
          })
          .catch(function () { /* stays unpriced (hidden) */ });
      })).then(function () {
        if (!document.contains(configEl)) return;     // swapped away meanwhile
        if (json) json.textContent = JSON.stringify(variants);
        if (currentVariant) showPrice(currentVariant);
        var cheapest = function (match) {
          var best = null;
          variants.forEach(function (v) { if (v.price != null && match(v) && (!best || v.price < best.price)) best = v; });
          return best;
        };
        configEl.querySelectorAll('[data-size-fieldset] [data-option-value]').forEach(function (chip) {
          var pos = parseInt(chip.getAttribute('data-option-position'), 10);
          var value = chip.getAttribute('data-value');
          var el = chip.querySelector('[data-size-price]');
          var best = cheapest(function (v) { return (v.options || [])[pos - 1] === value; });
          if (el && best) el.textContent = best.price_formatted || formatMoney(best.price);
        });
      });
    }

    // A Framed / Print tab whose offers were not all priced server-side renders no price and
    // lists its unpriced offer ids (snippets/frame-switcher). Price those, then state the
    // mode's true minimum. Any fetch failing leaves the tab without a price.
    function fillTabPrices() {
      var priceUrl = configEl.getAttribute('data-v2-price-url');
      if (!v2Line || !priceUrl || !window.fetch) return;
      var sep = priceUrl.indexOf('?') > -1 ? '&' : '?';
      configEl.querySelectorAll('[data-frame-switcher] [data-mode-missing]').forEach(function (el) {
        var ids = (el.getAttribute('data-mode-missing') || '').split(',').filter(Boolean);
        var known = el.hasAttribute('data-mode-known') ? { price: Number(el.getAttribute('data-mode-known')), price_formatted: el.getAttribute('data-mode-known-fmt') } : null;
        Promise.all(ids.map(function (id) {
          return fetch(priceUrl + sep + 'variant=' + encodeURIComponent(id) + '&view=v2-price', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        })).then(function (prices) {
          if (prices.some(function (p) { return !p || p.price == null; })) return;
          var best = known;
          prices.forEach(function (p) { if (!best || p.price < best.price) best = p; });
          if (best) el.textContent = (el.hasAttribute('data-mode-from') ? 'From ' : '') + (best.price_formatted || formatMoney(best.price));
        });
      });
    }

    function refresh(isUser) {
      var v = findVariant();
      currentVariant = v;
      if (v) {
        if (variantInput) variantInput.value = v.id;
        // Phase 10 hook for the live frame preview + size-on-the-wall (hd-frame.js,
        // hd-scale.js). Cancelable: when the frame preview shows the choice itself it
        // calls preventDefault(), and slide 0 keeps the room image instead of
        // switching to the variant's flat composite.
        var changeEvt = new CustomEvent('pdp:variant-change', {
          bubbles: true, cancelable: true,
          detail: { variant: v, isUser: !!isUser, selected: selected.slice() }
        });
        var keepDefault = configEl.dispatchEvent(changeEvt);
        // Variant-driven first image: on a shopper option change, show this
        // variant's image as slide 0 (revert to the template-composite if none).
        if (isUser && galleryApi && keepDefault) galleryApi.setVariantImage(variantImages[v.id] || null);
        showPrice(v);
        setCtaDisabled(!v.available);
        setCtaText(v.available ? 'Add to Cart' : 'Sold Out');
        // `?variant=` deep-links a variant of THIS product; a v2 variant id belongs to the
        // artist product, so it is not written to the artwork's URL.
        if (!v2Line) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', v.id);
            history.replaceState(history.state, '', url.toString());
          } catch (e) { /* no-op */ }
        }
      } else {
        setCtaDisabled(true);
        setCtaText('Unavailable');
      }
    }

    // Viewer selections use this same resolver, including offer IDs and signed cart quotes.
    configEl.addEventListener('pdp:select-variant', function (event) {
      var v = variants.find(function (item) { return event.detail && String(item.id) === event.detail.id && item.available; });
      if (!v) return;
      (v.options || []).forEach(function (value, index) {
        selected[index + 1] = value;
        groups.forEach(function (group) {
          if (Number(group.dataset.optionPosition) !== index + 1) return;
          group.querySelectorAll('[data-option-value]').forEach(function (button) { setPressed(button, button.dataset.value === value); });
        });
        updateSelectedLabel(index + 1, value);
      });
      refresh(true);
    });

    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      group.querySelectorAll('[data-option-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selected[pos] = btn.getAttribute('data-value');
          group.querySelectorAll('[data-option-value]').forEach(function (b) {
            setPressed(b, b === btn);
          });
          updateSelectedLabel(pos, btn.getAttribute('data-value'));
          refresh(true);
        });
      });
    });

    // Size "show N more" expander — bound here (not via an inline <script>) so it keeps
    // working after a frame-switch swap re-renders this panel through innerHTML, where
    // injected <script> tags never execute.
    var sizeToggle = configEl.querySelector('[data-size-toggle]');
    if (sizeToggle) {
      sizeToggle.addEventListener('click', function () {
        configEl.querySelectorAll('[data-size-extra]').forEach(function (b) { b.classList.remove('hidden'); });
        sizeToggle.classList.add('hidden');
        sizeToggle.setAttribute('aria-expanded', 'true');
      });
    }

    // "This is a gift" toggle reveals the message field. Bound here (not inline)
    // so it keeps working after a frame-switch swap re-renders this panel.
    var giftToggle = configEl.querySelector('[data-gift-toggle]');
    var giftNoteWrap = configEl.querySelector('[data-gift-note-wrap]');
    if (giftToggle && giftNoteWrap) {
      giftToggle.addEventListener('change', function () {
        giftNoteWrap.classList.toggle('hidden', !giftToggle.checked);
        if (giftToggle.checked) { var ta = giftNoteWrap.querySelector('[data-gift-note]'); if (ta) ta.focus(); }
      });
    }

    if (form) {
      var addInFlight = false;

      // Shopify's AJAX cart occasionally answers with a transient 429/503 (e.g. when
      // a burst of section fetches from rapid frame toggling briefly trips throttling).
      // Retry those a couple of times with backoff before surfacing an error, so a
      // momentary hiccup never blocks a real purchase. Network errors retry too.
      function addToCart(body, attempt) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          if (res.ok) return res.json();
          var transient = res.status === 429 || res.status >= 500;
          if (transient && attempt < 2) return backoff(attempt, body);
          return res.json().catch(function () { return null; }).then(function (data) {
            var err = new Error('Cart error ' + res.status);
            err.cartMessage = data && data.description;
            throw err;
          });
        }, function (netErr) {
          if (attempt < 2) return backoff(attempt, body);
          throw netErr;
        });
      }
      function backoff(attempt, body) {
        return new Promise(function (r) { setTimeout(r, 400 * (attempt + 1)); })
          .then(function () { return addToCart(body, attempt + 1); });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (addInFlight) return;              // guard against a double add on rapid taps
        var id = variantInput ? variantInput.value : null;
        if (!id) return;
        addInFlight = true;
        setCtaDisabled(true);
        setCtaText('Adding…');

        var body = { id: parseInt(id, 10), quantity: 1, properties: {} };
        if (v2Line) {
          // Without a signed quote AMS would refuse the line; never add an unsigned one.
          if (!currentVariant || !currentVariant.q || String(currentVariant.id) !== String(id)) {
            addInFlight = false;
            setCtaDisabled(false);
            setCtaText('Add to Cart');
            return;
          }
          Object.keys(v2Line).forEach(function (k) { body.properties[k] = v2Line[k]; });
          body.properties._sku = currentVariant.s;
          body.properties._quote = currentVariant.q;
        }
        var giftMsg = '';
        if (giftToggle && giftToggle.checked) {
          body.properties.Gift = 'Yes';
          var giftNote = configEl.querySelector('[data-gift-note]');
          if (giftNote && giftNote.value.trim()) giftMsg = giftNote.value.trim();
          // Also on the line itself, so a multi-item order keeps which piece the note belongs to.
          if (giftMsg) body.properties['Gift note'] = giftMsg;
        }

        addToCart(body, 0)
          // Gift message → order-level cart note (matches our other integrations, which read the order note).
          .then(function () {
            if (!giftMsg) return;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: '🎁 Gift message: ' + giftMsg })
            }).catch(function () { /* note is best-effort; don't block the add-to-cart success */ });
          })
          .then(function () {
            addInFlight = false;
            setCtaText('Added ✓');
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (window.ArthausCart && window.ArthausCart.openWithItem) {
              window.ArthausCart.openWithItem();
              setTimeout(function () {
                setCtaDisabled(false);
                setCtaText('Add to Cart');
              }, 1200);
            } else {
              setTimeout(function () { window.location.href = '/cart'; }, 500);
            }
          })
          .catch(function () {
            addInFlight = false;
            setCtaText('Error — try again');
            setCtaDisabled(false);
            setTimeout(function () { setCtaText('Add to Cart'); }, 2000);
          });
      });
    }

    refresh();
    fillMissingPrices();
    fillTabPrices();
  }

  function init(scope) {
    initGallery(scope || document);
    initConfig(scope || document);
  }

  document.addEventListener('DOMContentLoaded', function () {
    bindLightboxOnce();
    init(document);
  });

  return { init: init, initConfig: initConfig, initGallery: initGallery };
})();
